import { formatVenueDateYYYYMMDD } from "../dateUtils";
import { supabase, type User } from "../supabase";
interface SlotForBooking {
  date: string;
  time_start: string;
  time_end: string;
}

interface BookingForDate {
  id: string;
  status: "pending" | "approved" | "rejected";
  created_at: string;
  slot: SlotForBooking[]; // 👈 change to array
}
interface Slot {
  id: string;
  date: string;
  time_start: string;
  time_end: string;
  status: "pending" | "approved" | "rejected";
  booked_by_user_id?: string | null;
}

interface Booking {
  id: string;
  user_id: string;
  status: "pending" | "approved" | "rejected";
  rejection_reason?: string | null;
  created_at: string;
  slot: Slot;
}

export class UserService {
  // Create or get user by phone number
  static async createOrGetUser(
    phoneNumber: string,
    name?: string
  ): Promise<User | null> {
    try {
      // First, try to find existing user
      const { data: existingUser, error: findError } = await supabase
        .from("user")
        .select("*")
        .eq("phoneNo", phoneNumber)
        .single();

      if (existingUser && !findError) {
        if (name && existingUser.usernme !== name) {
          console.log(name, existingUser.username);
          const { data: updatedUser, error: updateError } = await supabase
            .from("user")
            .update({ username: name })
            .eq("phoneNo", phoneNumber)
            .select()
            .single();

          if (updateError) {
            console.error("Error updating username:", updateError);
            return existingUser;
          }
          return updatedUser;
        }
        return existingUser;
      }

      // If user doesn't exist, create new one
      const { data: newUser, error: createError } = await supabase
        .from("user")
        .insert([{ phoneNo: phoneNumber, username: name }])
        .select()
        .single();

      if (createError) {
        console.error("Error creating user:", createError);
        return null;
      }

      return newUser;
    } catch (error) {
      console.error("Error in createOrGetUser:", error);
      return null;
    }
  }

  // Get user by ID
  static async getUserById(userId: string): Promise<User | null> {
    try {
      const { data, error } = await supabase
        .from("user")
        .select("*")
        .eq("id", userId)
        .single();

      if (error) {
        console.error("Error getting user:", error);
        return null;
      }

      return data;
    } catch (error) {
      console.error("Error in getUserById:", error);
      return null;
    }
  }

  // Check if user has ANY booking for specific date (including pending, approved)
  static async hasBookingForDate(
    userId: string,
    date: string
  ): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from("bookings")
        .select(
          `
          id,
          status,
          slot:slot!inner(date)
        `
        )
        .eq("user_id", userId)
        .eq("slot.date", date)
        .in("status", ["pending", "approved"]); // Only check non-rejected bookings

      if (error) {
        console.error("Error checking user booking for date:", error);
        return false;
      }

      return data && data.length > 0;
    } catch (error) {
      console.error("Error in hasBookingForDate:", error);
      return false;
    }
  }

  // Get user's booking details for a specific date
  static async getBookingForDate(
    userId: string,
    date: string
  ): Promise<BookingForDate | null> {
    try {
      const { data, error } = await supabase
        .from("bookings")
        .select(
          `
        id,
        status,
        created_at,
        slot:slot!inner(date, time_start, time_end)
      `
        )
        .eq("user_id", userId)
        .eq("slot.date", date)
        .in("status", ["pending", "approved"])
        .single();

      if (error) {
        return null;
      }

      return data as BookingForDate;
    } catch (error) {
      console.error("Error in getBookingForDate:", error);
      return null;
    }
  }

  static async getActiveBookingDetails(
    userId: string
  ): Promise<Booking | null> {
    try {
      const { data, error } = await supabase
        .from("bookings")
        .select(
          `
        *,
        slot:slot(*)
      `
        )
        .eq("user_id", userId)
        .neq("status", "rejected")
        .gte("slot.date", formatVenueDateYYYYMMDD(new Date()))
        .order("slot.date", { ascending: true })
        .limit(1)
        .single();

      if (error) throw error;
      return data as Booking;
    } catch (error) {
      console.error("Error in getActiveBookingDetails:", error);
      return null;
    }
  }

  static async hasActiveBooking(userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from("bookings")
        .select("id, slot:slot_id(date)")
        .eq("user_id", userId)
        .neq("status", "rejected")
        .gte("slot.date", formatVenueDateYYYYMMDD(new Date())); // Only future bookings

      if (error) throw error;
      return data && data.length > 0;
    } catch (error) {
      console.error("Error in hasActiveBooking:", error);
      return false;
    }
  }
}
